import { ValidatorFn, Validators } from '@angular/forms';
import { RecentsApiResponse } from './models';
import { FileItem } from './models/files.model';

/**
 * Compare function used for sort() functions.
 * @param a Object 1
 * @param b Object 2
 * @param isAsc is ascending
 */
export function compare(a, b, isAsc: boolean): number {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

/**
 * Sorts web paths.
 * @param a Webpath 1
 * @param b WebPath 2
 * @param isAsc boolean is ascending sort
 * @param dirsFirst  boolean should sort dirs first
 */
export function compareWebPath(a: FileItem, b: FileItem, isAsc: boolean, dirsFirst: boolean): number {
  let result: number;
  if (isAsc) {
    result = b.name.toString().localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' });
  } else {
    result = a.name.toString().localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
  }
  if (dirsFirst) {
    return b.is_dir - a.is_dir || result;
  } else {
    return result;
  }
}

export function getFileExt(fileName: string): string {
  return fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();
}

export function canBrowserOpen(fileName: string): boolean {
  let openable = true;
  switch (getFileExt(fileName)) {
    case 'pdf':
    case 'txt':
    case 'md':
    case 'jpg':
    case 'gif':
    case 'png':
      openable = true;
      break;
    case 'wopitest':
    case 'wopitestx':
    case '7z':
    case 'zip':
    case 'rar':
    case 'gz':
    case 'gzip':
    case 'tar':
    case 'bz2':
    case 'tgz':
    case 'tbz2':
    case 'dmg':
    case 'exe':
    case 'bin':
    case 'bat':
    case 'iso':
    case 'heic':
    case 'heif':
    case 'hevc':
    case 'wmv':
    case 'avi':
    case 'flv':
    case 'mpg':
    case 'fig':
    case 'flac':
    //video file type set false
    case 'mp4':
    case 'webm':
    case 'avi':
    case 'mov':
    case 'mkv':
    case 'wmv':
    case 'flv':
    case 'mpeg':
    case 'mpg':
    case '3gp':
    // Audio file types set false
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'ogg':
    case 'aac':
    case 'wma':
    case 'alac':
    case 'aiff':
    case 'mid':
    case 'midi':
    case 'asf':
    case 'm4a':
      openable = false;
      break;
    default:
      openable = true;
  }
  return openable;
}
export function canLinkBrowserOpen(fileName: string): boolean {
  let openable = false;
  switch (getFileExt(fileName)) {
    case 'pdf':
    case 'txt':
    case 'md':
    case 'jpg':
    case 'gif':
    case 'png':
    case 'mp3':
    case 'mp4':
    case 'avi':
    case 'mov':
      openable = true;
      break;
    case 'wopitest':
    case 'wopitestx':
    case '7z':
    case 'zip':
    case 'rar':
    case 'gz':
    case 'gzip':
    case 'tar':
    case 'bz2':
    case 'tgz':
    case 'tbz2':
    case 'dmg':
    case 'exe':
    case 'bin':
    case 'bat':
    case 'iso':
    case 'heic':
    case 'heif':
    case 'hevc':
    case 'wmv':
    case 'avi':
    case 'flv':
    case 'mpg':
    case 'fig':
    case 'flac':
      openable = false;
      break;
    default:
      openable = true;
  }
  return openable;
}

export function comparePathItemsName(a: RecentsApiResponse, b: RecentsApiResponse, isAsc: boolean): number {
  const nameA = a.items.pathitems[0].name;
  const nameB = b.items.pathitems[0].name;

  if (isAsc) {
    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
  } else {
    return nameB.localeCompare(nameA, undefined, { numeric: true, sensitivity: 'base' });
  }
}

/**
 * Angular forms validator that will make a field required if predicate()
 * function returns true
 */
export const requiredIfValidator = (predicate: () => boolean): ValidatorFn => {
  return (formControl) => {
    if (!formControl.parent) {
      return null;
    }
    if (predicate()) {
      return Validators.required(formControl);
    }
    return null;
  };
};

export const requiredTrimmedValidator: ValidatorFn = (formControl) =>
  String(formControl.value ?? '').trim() ? null : { required: true };

/**
 * Angular forms validator that accepts any validator function if predicate()
 * returns true.
 */
export const conditionalValidator = (predicate: () => boolean, validator: ValidatorFn): ValidatorFn => {
  return (formControl) => {
    if (!formControl.parent) {
      return null;
    }
    if (predicate()) {
      return validator(formControl);
    }
    return null;
  };
};

export const bytesToGB = (bytes: number, decimalPlaces = 2): string => {
  return (bytes / 1024 ** 3).toFixed(decimalPlaces);
};
